import React from 'react';
import {
  CurrencyDollarIcon,
  CpuChipIcon,
  ChatBubbleLeftRightIcon,
  CreditCardIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

export default function BillingHelp() {
  return (
    <div className="max-w-4xl mx-auto p-6 bg-gradient-to-br from-slate-50 to-green-50 rounded-lg shadow-lg">
      <div className="text-center mb-8">
        <CurrencyDollarIcon className="mx-auto h-16 w-16 text-green-600 mb-4" />
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Billing & Subscription Management</h1>
        <p className="text-lg text-gray-600">Manage your billing, AI token usage, and subscription services</p>
      </div>

      {/* Overview Section */}
      <div className="bg-white rounded-lg p-6 mb-6 shadow-md">
        <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
          <CreditCardIcon className="h-6 w-6 text-green-600 mr-2" />
          Overview
        </h2>
        <p className="text-gray-700 mb-4">
          The Billing Management interface provides comprehensive control over your subscription, payment history, AI token usage, and billing support.
          Monitor costs, manage AI token allocations, view billing history, and access support for billing-related inquiries.
          The system offers transparent pricing and real-time usage tracking.
        </p>
        <div className="bg-green-50 border-l-4 border-green-400 p-4">
          <p className="text-sm text-green-700">
            <strong>Key Features:</strong> Subscription management, AI token purchasing, billing history, usage analytics, and dedicated billing support.
          </p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg p-6 mb-6 shadow-md">
        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Navigation Tabs</h2>
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button className="border-indigo-500 text-indigo-600 whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm opacity-75 cursor-not-allowed">
              Billing
            </button>
            <button className="border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm opacity-75 cursor-not-allowed">
              AI Tokens
            </button>
            <button className="border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm opacity-75 cursor-not-allowed">
              Billing Support
            </button>
          </nav>
        </div>

        {/* Billing Tab Content */}
        <div className="mb-8">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Billing Dashboard</h3>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CpuChipIcon className="h-8 w-8 text-blue-600" />
                </div>
                <div className="ml-4">
                  <dt className="text-sm font-medium text-blue-600 truncate">AI Token Usage</dt>
                  <dd className="text-2xl font-semibold text-blue-900">$12.50/$15.00</dd>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-6 border border-green-200">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ChartBarIcon className="h-8 w-8 text-green-600" />
                </div>
                <div className="ml-4">
                  <dt className="text-sm font-medium text-green-600 truncate">AI Requests</dt>
                  <dd className="text-2xl font-semibold text-green-900">1,247</dd>
                </div>
              </div>
            </div>
          </div>

          {/* Billing History Table */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-md font-medium text-gray-900 mb-3">Recent Transactions</h4>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Oct 15, 2025</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">AI Token Purchase - 100 tokens</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">$10.00</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Paid
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Oct 1, 2025</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Monthly Subscription</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">$15.00</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Paid
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* AI Tokens Tab Content */}
        <div className="mb-8">
          <h3 className="text-lg font-medium text-gray-900 mb-4">AI Token Management</h3>

          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-6 border border-purple-200 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h4 className="text-lg font-medium text-purple-900">Purchase AI Tokens</h4>
                <p className="text-sm text-purple-700">Buy additional AI tokens for enhanced platform features</p>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-purple-900">$1.00</div>
                <div className="text-sm text-purple-600">per token</div>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
              {[10, 20, 30, 40, 50, 100].map((amount) => (
                <button
                  key={amount}
                  className="px-4 py-2 border border-purple-300 rounded-md text-sm font-medium text-purple-700 bg-white hover:bg-purple-50 opacity-75 cursor-not-allowed"
                >
                  {amount} tokens
                </button>
              ))}
            </div>

            <div className="flex items-center justify-between">
              <div className="text-sm text-purple-600">
                Selected: <span className="font-medium">20 tokens</span> - Total: <span className="font-medium">$20.00</span>
              </div>
              <button className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 opacity-75 cursor-not-allowed">
                Purchase Tokens
              </button>
            </div>
          </div>

          {/* Token Usage Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <div className="text-2xl font-bold text-gray-900">1,247</div>
              <div className="text-sm text-gray-500">Total Requests</div>
            </div>
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <div className="text-2xl font-bold text-gray-900">$12.50</div>
              <div className="text-sm text-gray-500">Tokens Used</div>
            </div>
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <div className="text-2xl font-bold text-gray-900">$2.50</div>
              <div className="text-sm text-gray-500">Remaining</div>
            </div>
          </div>
        </div>

        {/* Billing Support Tab Content */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Billing Support</h3>

          <div className="space-y-6">
            {/* Support Options */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-md font-medium text-gray-900 mb-3">Support Type</h4>
              <div className="space-y-2">
                <button className="w-full text-left px-4 py-3 border border-gray-300 rounded-md hover:bg-gray-50 opacity-75 cursor-not-allowed">
                  <div className="font-medium text-gray-900">Upgrade Plan</div>
                  <div className="text-sm text-gray-500">Increase user count or upgrade subscription tier</div>
                </button>
                <button className="w-full text-left px-4 py-3 border border-gray-300 rounded-md hover:bg-gray-50 opacity-75 cursor-not-allowed">
                  <div className="font-medium text-gray-900">Cancellation</div>
                  <div className="text-sm text-gray-500">Cancel subscription or modify billing</div>
                </button>
              </div>
            </div>

            {/* Upgrade Plan Form */}
            <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
              <h4 className="text-lg font-medium text-blue-900 mb-4">Upgrade Plan</h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-blue-700 mb-2">Additional Users</label>
                  <input
                    type="number"
                    className="w-full px-3 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 opacity-75 cursor-not-allowed"
                    placeholder="Enter number of users"
                    disabled
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-blue-700 mb-2">Total Cost</label>
                  <div className="px-3 py-2 border border-blue-300 rounded-md bg-white text-blue-900 font-medium">
                    $45.00 (3 users × $15.00)
                  </div>
                </div>
              </div>

              <button className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 opacity-75 cursor-not-allowed">
                Upgrade Plan
              </button>
            </div>

            {/* Support Message Form */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h4 className="text-lg font-medium text-gray-900 mb-4">Support Message</h4>

              <div className="mb-4">
                <textarea
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 opacity-75 cursor-not-allowed"
                  placeholder="Describe your billing issue or question..."
                  disabled
                />
              </div>

              <button className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 opacity-75 cursor-not-allowed">
                Submit Support Request
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Key Features */}
      <div className="grid md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white rounded-lg p-6 shadow-md">
          <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <CurrencyDollarIcon className="h-5 w-5 text-green-600 mr-2" />
            Billing Management
          </h3>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start">
              <span className="text-green-500 mr-2">•</span>
              Real-time usage tracking
            </li>
            <li className="flex items-start">
              <span className="text-green-500 mr-2">•</span>
              Detailed transaction history
            </li>
            <li className="flex items-start">
              <span className="text-green-500 mr-2">•</span>
              Cost analysis and reporting
            </li>
            <li className="flex items-start">
              <span className="text-green-500 mr-2">•</span>
              Payment method management
            </li>
          </ul>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-md">
          <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <CpuChipIcon className="h-5 w-5 text-purple-600 mr-2" />
            AI Token System
          </h3>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start">
              <span className="text-purple-500 mr-2">•</span>
              Flexible token purchasing
            </li>
            <li className="flex items-start">
              <span className="text-purple-500 mr-2">•</span>
              Usage monitoring and limits
            </li>
            <li className="flex items-start">
              <span className="text-purple-500 mr-2">•</span>
              Cost-effective AI access
            </li>
            <li className="flex items-start">
              <span className="text-purple-500 mr-2">•</span>
              Automatic usage alerts
            </li>
          </ul>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-md">
          <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <ChatBubbleLeftRightIcon className="h-5 w-5 text-blue-600 mr-2" />
            Support Services
          </h3>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start">
              <span className="text-blue-500 mr-2">•</span>
              Dedicated billing support
            </li>
            <li className="flex items-start">
              <span className="text-blue-500 mr-2">•</span>
              Plan upgrade assistance
            </li>
            <li className="flex items-start">
              <span className="text-blue-500 mr-2">•</span>
              Cancellation processing
            </li>
            <li className="flex items-start">
              <span className="text-blue-500 mr-2">•</span>
              Technical billing issues
            </li>
          </ul>
        </div>
      </div>

      {/* Usage Guidelines */}
      <div className="bg-white rounded-lg p-6 shadow-md">
        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Usage Guidelines</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">Billing Best Practices</h3>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                Monitor AI token usage regularly
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                Set up usage alerts and limits
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                Review billing history monthly
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                Plan token purchases in advance
              </li>
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">Support Guidelines</h3>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <span className="text-blue-500 mr-2">•</span>
                Provide detailed issue descriptions
              </li>
              <li className="flex items-start">
                <span className="text-blue-500 mr-2">•</span>
                Include relevant account information
              </li>
              <li className="flex items-start">
                <span className="text-blue-500 mr-2">•</span>
                Specify preferred resolution timeline
              </li>
              <li className="flex items-start">
                <span className="text-blue-500 mr-2">•</span>
                Check billing dashboard first for common issues
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-6 bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mr-2" />
            <div>
              <p className="text-sm text-yellow-700">
                <strong>Important:</strong> AI token purchases are final and cannot be refunded.
                Monitor your usage to avoid unexpected charges. Contact support for billing disputes within 30 days of the transaction.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}